/* SpawnAnything — structural repair for AI unit recipes ("Bauplan-Reparatur").
   The LLM places `add` entries in part-relative units and often gets the geometry slightly wrong: parts hover next
   to the body (crowns above the head, props below the hand, ear tips above the ears), sit on the wrong bone (a skirt
   on both legs, a tail spike on the body), sink below the ground (shoes at legs y -1.5) or are thinner than a voxel.
   repairRecipe() rasterizes the figure the way the game does (compileRecipe + fillPrim, faceMode 'aus') and fixes
   the recipe itself, so every client that receives the repaired recipe builds exactly the same model.
   Pure and deterministic (no randomness, no clock); only meant for own AI figures - hand-made recipes and the
   curated library are never passed through it (see repairOwnRecipe).

   Terms: an "entry" is one element of rec.add; it compiles to one or more "copies" (mirror, arms/legs pairs, the
   four quadruped legs, serpent segments). A "group" is a set of parts that move together in the animation shader
   (main.js animUnit). Every copy must touch the anchor (the template's own primitives) of its group through copies
   of the same group - otherwise it hovers in the rest pose or tears off when the part swings. */
import { compileRecipe } from './voxel.js';
import { quadrupedVoxelSize } from './templates/quadruped.js';
import { fillPrim } from './heads.js';
import { UNIT_TEMPLATES, MAX_ADD, AT_RANGE, SIZE_RANGE, validateUnitRecipe } from './recipeSchema.js';

/* bump when the rules change; repaired recipes carry rec.fixed = REPAIR_VERSION ('lib' = curated, never touched) */
export const REPAIR_VERSION = 1;

const round4 = v => Math.round(v * 1e4) / 1e4 || 0;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const clone = o => JSON.parse(JSON.stringify(o));

/* voxel size the game rasterizes a recipe at: detail 1 (far LOD) or 2 (near LOD), see recipeModel / quadrupedModel */
export function voxelSizeOf(rec, t = 0.4, detail = 1) {
  const b = rec.build || {};
  if (rec.template === 'quadruped') { const vs = quadrupedVoxelSize(b); return detail > 1 && vs < 0.09 ? vs / 2 : vs; }
  const vs = rec.template === 'serpent' ? 0.14 : (1.8 / 22) / (1 + t * 0.6);
  return detail > 1 ? vs / 2 : vs;
}

/* motion groups: part index -> group id (0 = static root) */
function groupOf(tpl, part) {
  if (tpl === 'humanoid') return part <= 1 ? 0 : part; // torso/pelvis + head (it barely nods) | each arm | each leg
  if (tpl === 'serpent') return part >= 3 ? 3 : part; // segments move as one wave (their base prims overlap)
  return part; // quadruped: body | head + neck | jaw | four legs | tail
}
// part name an entry is re-hung to when it has to move with group g ('' = not possible)
function partForGroup(tpl, g, y, frames) {
  if (tpl === 'humanoid') {
    if (g === 0) return frames.torso && y >= frames.torso.c[1] - frames.torso.h[1] ? 'torso' : 'pelvis';
    return ['', '', 'armL', 'armR', 'legL', 'legR'][g] || '';
  }
  if (tpl === 'quadruped') return ['body', 'head', 'jaw', 'legFL', 'legFR', 'legBL', 'legBR', 'tail'][g] || '';
  if (tpl === 'serpent') return g === 1 ? 'head' : g === 2 ? 'jaw' : '';
  return '';
}
/* ---------------- scene: compile + rasterize every primitive on its own ---------------- */
// every add entry gets colour #000001 + index, so each compiled primitive tells which entry (and copy) it came from;
// template colours become grey (keeping the keys: an optional belly is a template primitive too)
const tagHex = i => '#' + (i + 1).toString(16).padStart(6, '0');
const greyColors = rec => Object.fromEntries(Object.keys(rec.colors || {}).map(k => [k, '#888888']));
function tagged(rec, atFn) {
  return {
    ...rec, faceMode: 'aus', colors: greyColors(rec),
    add: (rec.add || []).map((a, i) => {
      const o = { ...a, color: tagHex(i) };
      delete o.stripes; delete o.checker;
      if (atFn) o.at = atFn(a);
      return o;
    }),
  };
}
function buildOf(rec) {
  const b = { ...(rec.build || {}) };
  b.width = b.width || 1; b.headScale = b.headScale || 1;
  return b;
}
// half extents of a primitive's world AABB (rotated prims: the rotated box corners)
function aabbHalf(p) {
  if (!p.inv) return p.h.slice();
  const M = p.inv; // inverse rotation = transpose of the rotation: world axis a = row a of R = column a of inv
  return [0, 1, 2].map(a => Math.abs(M[a * 4]) * p.h[0] + Math.abs(M[a * 4 + 1]) * p.h[1] + Math.abs(M[a * 4 + 2]) * p.h[2]);
}

/**
 * Compile and rasterize a recipe primitive by primitive.
 * @param {object} rec  game-format unit recipe
 * @param {{ t?: number, detail?: number }} [o]
 * @returns {{ vs, t, prims, frames }}  prims[i] = { i, entry (-1 = template base), copy, part, group, base, c, h, e,
 *   J (d centre / d at, metres per unit, negative x on mirrored copies), shape, rot, cells (cell keys) };
 *   frames[on] = { c, h, part } for every part name of the template.
 */
export function buildScene(rec, { t = 0.4, detail = 1 } = {}) {
  const b = buildOf(rec), tpl = rec.template;
  const vs = voxelSizeOf(rec, t, detail);
  const full = compileRecipe(tagged(rec), t, b).prims;
  const zero = compileRecipe(tagged(rec, () => [0, 0, 0]), t, b).prims;
  const one = compileRecipe(tagged(rec, () => [1, 1, 1]), t, b).prims;
  const prims = [], copyN = new Map(), nAdd = (rec.add || []).length;
  const sink = { keys: null, set(k) { this.keys.push(k); } };
  for (let i = 0; i < full.length; i++) {
    const p = full[i];
    const entry = p.color >= 1 && p.color <= nAdd ? p.color - 1 : -1;
    const copy = entry >= 0 ? (copyN.get(entry) || 0) : 0;
    if (entry >= 0) copyN.set(entry, copy + 1);
    sink.keys = [];
    fillPrim(sink, p, vs);
    prims.push({ i, entry, copy, part: p.part, group: groupOf(tpl, p.part), base: entry < 0, c: p.c, h: p.h, e: aabbHalf(p),
      J: [0, 1, 2].map(a => one[i].c[a] - zero[i].c[a]), shape: p.shape, inv: p.inv, cells: sink.keys });
  }
  // part frames: one unit box per part name
  const parts = UNIT_TEMPLATES[tpl] ? UNIT_TEMPLATES[tpl].parts : [];
  const probe = compileRecipe({ ...rec, faceMode: 'aus', colors: greyColors(rec), add: parts.map((on, i) => ({ on, shape: 'box', color: tagHex(i) })) }, t, b).prims;
  const frames = {};
  for (const p of probe) {
    const k = p.color - 1;
    if (k >= 0 && k < parts.length && !frames[parts[k]]) frames[parts[k]] = { c: p.c, h: p.h, part: p.part };
  }
  return { vs, t, prims, frames, tpl };
}

/* ---------------- connectivity on a dense grid ---------------- */
function makeGrid(prims) {
  const lo = [1e9, 1e9, 1e9], hi = [-1e9, -1e9, -1e9];
  for (const p of prims) for (const k of p.cells) {
    const x = (k & 1023) - 512, y = ((k >> 10) & 1023) - 512, z = ((k >> 20) & 1023) - 512;
    if (x < lo[0]) lo[0] = x; if (x > hi[0]) hi[0] = x; if (y < lo[1]) lo[1] = y; if (y > hi[1]) hi[1] = y; if (z < lo[2]) lo[2] = z; if (z > hi[2]) hi[2] = z;
  }
  if (lo[0] > hi[0]) return null;
  for (let a = 0; a < 3; a++) { lo[a] -= 1; hi[a] += 1; }
  const NX = hi[0] - lo[0] + 1, NY = hi[1] - lo[1] + 1, NZ = hi[2] - lo[2] + 1, N = NX * NY * NZ;
  const li = k => ((k & 1023) - 512 - lo[0]) + (((k >> 10) & 1023) - 512 - lo[1]) * NX + (((k >> 20) & 1023) - 512 - lo[2]) * NX * NY;
  let total = 0; for (const p of prims) total += p.cells.length;
  const head = new Int32Array(N).fill(-1), next = new Int32Array(total), who = new Int32Array(total);
  const occ = [];
  let n = 0;
  for (const p of prims) for (const k of p.cells) {
    const c = li(k);
    if (head[c] < 0) occ.push(c);
    who[n] = p.i; next[n] = head[c]; head[c] = n++;
  }
  const off = [];
  for (let z = -1; z <= 1; z++) for (let y = -1; y <= 1; y++) for (let x = -1; x <= 1; x++) if (x || y || z) off.push(x + y * NX + z * NX * NY);
  const xyz = c => [c % NX + lo[0], Math.floor(c / NX) % NY + lo[1], Math.floor(c / (NX * NY)) + lo[2]];
  return { lo, NX, NY, NZ, N, li, head, next, who, occ, off, xyz };
}
/* touch[i] = Set of prims j sharing or neighbouring (26) a cell with prim i. Cells are grouped by the set of prims
   that cover them; every pair of neighbouring cell sets is linked once (most cells repeat a handful of sets). */
function contacts(prims, G) {
  const n = prims.length, M = new Uint8Array(n * n);
  const touch = prims.map(() => new Set());
  const link = (a, b) => { if (a !== b && !M[a * n + b]) { M[a * n + b] = M[b * n + a] = 1; touch[a].add(b); touch[b].add(a); } };
  const { head, next, who, occ } = G;
  const sid = new Int32Array(G.N).fill(-1), ids = new Map(), sets = [];
  for (const c of occ) {
    let key = '';
    for (let u = head[c]; u >= 0; u = next[u]) key += who[u] + ',';
    let k = ids.get(key);
    if (k === undefined) {
      k = sets.length; ids.set(key, k);
      const l = []; for (let u = head[c]; u >= 0; u = next[u]) l.push(who[u]);
      sets.push(l);
      for (let x = 0; x < l.length; x++) for (let y = x + 1; y < l.length; y++) link(l[x], l[y]);
    }
    sid[c] = k;
  }
  const S = sets.length, done = new Set(), fwd = G.off.filter(d => d > 0);
  for (const c of occ) {
    const s1 = sid[c];
    for (let q = 0; q < fwd.length; q++) {
      const s2 = sid[c + fwd[q]];
      if (s2 < 0 || s2 === s1) continue;
      const key = s1 < s2 ? s1 * S + s2 : s2 * S + s1;
      if (done.has(key)) continue;
      done.add(key);
      for (const a of sets[s1]) for (const b of sets[s2]) link(a, b);
    }
  }
  return touch;
}
class DSU {
  constructor(n) { this.p = Array.from({ length: n }, (_, i) => i); }
  find(a) { while (this.p[a] !== a) { this.p[a] = this.p[this.p[a]]; a = this.p[a]; } return a; }
  union(a, b) { a = this.find(a); b = this.find(b); if (a !== b) { if (a < b) this.p[b] = a; else this.p[a] = b; } }
}

/**
 * Which copies are not anchored to their motion group.
 * @returns {{ loose: {group, prims: number[], cells: number}[], empty: number[], anchored: Uint8Array, touch, G }}
 *   loose: connected sets of copies of one group that do not reach the group's template primitives;
 *   empty: copies without a single voxel (too thin or below the ground).
 */
export function analyzeScene(sc) {
  const { prims } = sc;
  const G = makeGrid(prims);
  const anchored = new Uint8Array(prims.length);
  if (!G) return { loose: [], empty: prims.filter(p => !p.base).map(p => p.i), anchored, touch: prims.map(() => new Set()), G };
  const touch = contacts(prims, G);
  const dsu = new DSU(prims.length);
  for (const p of prims) for (const j of touch[p.i]) if (prims[j].group === p.group) dsu.union(p.i, j);
  const roots = new Set();
  for (const p of prims) if (p.base && p.cells.length) roots.add(dsu.find(p.i));
  const comps = new Map(), empty = [];
  for (const p of prims) {
    if (!p.cells.length) { if (!p.base) empty.push(p.i); continue; }
    const r = dsu.find(p.i);
    if (roots.has(r)) { anchored[p.i] = 1; continue; }
    if (!comps.has(r)) comps.set(r, { group: p.group, prims: [], cells: 0 });
    const cp = comps.get(r); cp.prims.push(p.i); cp.cells += p.cells.length;
  }
  return { loose: [...comps.values()], empty, anchored, touch, G };
}

/* Shortest move (in cells) that makes a loose set touch the anchored prims of `group` (26-BFS over the grid).
   Returns { d: [dx, dy, dz], dist } with the set face-adjacent to the target afterwards, or null. */
function dockMove(sc, an, comp, group) {
  const { G } = an, { prims } = sc;
  const isTarget = c => { for (let u = G.head[c]; u >= 0; u = G.next[u]) { const j = G.who[u]; if (an.anchored[j] && prims[j].group === group) return true; } return false; };
  const seen = new Int32Array(G.N).fill(-1); // origin cell (source) per visited cell
  let q = [];
  for (const i of comp.prims) for (const k of prims[i].cells) { const c = G.li(k); if (seen[c] < 0) { seen[c] = c; q.push(c); } }
  // the grid is padded by one cell only: stop at its border (targets are inside it)
  const inside = c => { const x = c % G.NX, y = Math.floor(c / G.NX) % G.NY, z = Math.floor(c / (G.NX * G.NY)); return x > 0 && y > 0 && z > 0 && x < G.NX - 1 && y < G.NY - 1 && z < G.NZ - 1; };
  let dist = 0;
  while (q.length) {
    dist++;
    const nq = [];
    let best = null, bestD2 = Infinity;
    for (const c of q) {
      if (!inside(c)) continue;
      for (const d of G.off) {
        const n = c + d;
        if (seen[n] >= 0) continue;
        seen[n] = seen[c];
        if (isTarget(n)) {
          const s = G.xyz(seen[n]), t = G.xyz(n), v = [t[0] - s[0], t[1] - s[1], t[2] - s[2]];
          const d2 = v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
          if (d2 < bestD2 || (d2 === bestD2 && n < best.n)) { bestD2 = d2; best = { v, n }; }
        } else nq.push(n);
      }
    }
    if (best) {
      // stop one cell short along the dominant axis: face contact instead of overlap
      const v = best.v, ax = [0, 1, 2].reduce((m, a) => Math.abs(v[a]) > Math.abs(v[m]) ? a : m, 0);
      v[ax] -= Math.sign(v[ax]);
      return { d: v, dist };
    }
    q = nq;
  }
  return null;
}

/* ---------------- recipe edits ---------------- */
const inRange = v => clamp(round4(v), AT_RANGE[0], AT_RANGE[1]);
const sizeRange = v => clamp(round4(v), SIZE_RANGE[0], SIZE_RANGE[1]);
// move entry e by a world offset (metres) seen on copy p
function shiftEntry(a, p, dm) {
  const at = (a.at || [0, 0, 0]).slice();
  for (let k = 0; k < 3; k++) if (dm[k] && p.J[k]) at[k] = inRange(at[k] + dm[k] / p.J[k]);
  a.at = at;
}
/* one entry on part `on` with frame F that reproduces copy p (same world centre and half size) */
function entryForCopy(a, p, on, F, mirrored) {
  const o = clone(a);
  o.on = on;
  o.at = [0, 1, 2].map(k => inRange((p.c[k] - F.c[k]) / F.h[k]));
  o.size = [0, 1, 2].map(k => sizeRange(p.h[k] / F.h[k]));
  delete o.mirror;
  if (mirrored && o.rot) o.rot = [o.rot[0], round4(-o.rot[1]) || 0, round4(-o.rot[2]) || 0];
  return o;
}
/* Re-hang the copies of entry `a` to the static/other part `on`; returns the replacement entries (1-2). */
function rehang(a, copies, on, F) {
  const right = copies.filter(p => p.c[0] - F.c[0] >= 0), left = copies.filter(p => p.c[0] - F.c[0] < 0);
  const sym = (p, q) => Math.abs((p.c[0] - F.c[0]) + (q.c[0] - F.c[0])) < 1e-6 && Math.abs(p.c[1] - q.c[1]) < 1e-6 && Math.abs(p.c[2] - q.c[2]) < 1e-6;
  const out = [];
  const used = new Set();
  for (const p of right.concat(left)) {
    if (used.has(p)) continue;
    used.add(p);
    const mirrored = p.J[0] < 0;
    const twin = copies.find(q => !used.has(q) && sym(p, q));
    if (twin) {
      used.add(twin);
      const r = p.c[0] >= twin.c[0] ? p : twin;
      const o = entryForCopy(a, r, on, F, r.J[0] < 0);
      // the two copies overlap across the middle: one centred entry spanning both
      if (Math.abs(r.c[0] - F.c[0]) <= r.e[0]) { o.at[0] = 0; o.size[0] = sizeRange((Math.abs(r.c[0] - F.c[0]) + r.h[0]) / F.h[0]); }
      else o.mirror = true;
      out.push(o);
    } else out.push(entryForCopy(a, p, on, F, mirrored));
  }
  return out;
}

/**
 * Repair the geometry of an AI unit recipe. Never throws; deterministic.
 * @param {object} rec  validated game-format recipe (validateUnitRecipe output)
 * @param {{ t?: number }} [opts]
 * @returns {{ recipe: object, fixes: string[], detail: number }}  recipe: repaired copy (the input is not modified);
 *   detail: the LOD the gaps were judged on (see analysisDetail)
 */
export function repairRecipe(rec, opts = {}) {
  const fixes = [];
  try {
    if (!rec || typeof rec !== 'object' || !UNIT_TEMPLATES[rec.template] || !Array.isArray(rec.add) || !rec.add.length) return { recipe: rec, fixes, detail: 1 };
    const r = clone(rec);
    const t = opts.t ?? 0.4;
    const detail = analysisDetail(r, t); // decided once, on the input
    groundAndThin(r, t, fixes);
    toStaticBody(r, t, fixes);
    let res = CHANGED;
    for (let pass = 0; pass < 4 && res === CHANGED; pass++) res = attachPass(r, t, detail, fixes, pass);
    if (res !== CLEAN) dropLoose(r, t, detail, fixes);
    // lifted or moved parts may vanish on the far LOD: thicken them (only adds voxels, nothing comes loose)
    const far = buildScene(r, { t, detail: 1 });
    fixThin(r, far, far.prims.filter(p => !p.base && !p.cells.length), fixes);
    // other proportions (realism slider) and the far LOD can open one-voxel gaps where the main view touches:
    // close them with small moves, but never at the cost of the main view (which has nothing loose now)
    const looseMain = () => analyzeScene(buildScene(r, { t, detail })).loose.length;
    const views = r.template === 'humanoid' ? [[1, detail], [0, detail], [t, 1]] : [[t, 1]]; // only humanoids use the slider
    for (const [tt, dd] of views) {
      if (tt === t && dd === detail) continue;
      const keep = JSON.stringify(r.add), nf = fixes.length;
      for (let k = 0; k < 2 && attachPass(r, tt, dd, fixes, 0, true) === CHANGED; k++);
      if (fixes.length > nf && looseMain() > 0) { r.add = JSON.parse(keep); fixes.length = nf; }
    }
    // re-hung and thickened parts can push the voxel estimate over the budget: shrink like the validator does, so every
    // client validates the repaired recipe without changes (multiplayer, replays), then close what shrinking opened
    const v = validateUnitRecipe(r);
    if (v.errors.length) {
      Object.assign(r, v.recipe);
      fixes.push('Größen ans Voxel-Budget angepasst');
      attachPass(r, t, detail, fixes, 0, true);
      dropLoose(r, t, detail, fixes);
    }
    return { recipe: r, fixes, detail };
  } catch (e) {
    return { recipe: rec, fixes: ['internal: ' + String(e && e.message || e).slice(0, 80)], detail: 1 };
  }
}

const CELL_BUDGET = 48000; // primitive cells of one analysis scene (~10 ms)
/** LOD the repair judges gaps on: the near LOD (detail 2), unless that gets too big (giant beasts, long serpents). */
export function analysisDetail(rec, t = 0.4) {
  let cells = 0;
  for (const p of buildScene(rec, { t, detail: 1 }).prims) cells += p.cells.length;
  return cells * 8 <= CELL_BUDGET ? 2 : 1;
}
/** Number of loose part sets (hovering or torn off when animated) of a recipe in one view. */
export function looseParts(rec, { t = 0.4, detail = analysisDetail(rec, t) } = {}) {
  return analyzeScene(buildScene(rec, { t, detail })).loose.length;
}
const SPECK_CELLS = 8, SPECK_DIST = 6; // loose sets this small and this far (cells) are dropped, not docked
const CLEAN = 0, CHANGED = 1, STUCK = 2; // attachPass results
/* 1. copies below the ground are lifted onto it; copies too thin to show a single voxel become boxes */
function groundAndThin(r, t, fixes) {
  const sc = buildScene(r, { t, detail: 1 });
  const lift = new Map(), thin = new Set();
  for (const p of sc.prims) {
    if (p.base) continue;
    if (p.c[1] < 0 && p.J[1] > 0) {
      // more than half below the ground (shoes at legs y -1.5): put its bottom onto the ground
      const need = (p.e[1] - p.c[1]) / p.J[1];
      lift.set(p.entry, Math.max(lift.get(p.entry) || 0, need));
    }
  }
  for (const [i, need] of lift) {
    const a = r.add[i], at = (a.at || [0, 0, 0]).slice();
    at[1] = inRange(at[1] + need + 1e-4);
    a.at = at;
    fixes.push(`add[${i}] (${a.on}): unter dem Boden -> angehoben`);
  }
  fixThin(r, sc, sc.prims.filter(p => !p.base && !p.cells.length && !lift.has(p.entry)), fixes);
}
/* copies that vanish on a LOD although they are above the ground: round shapes become boxes (a box always keeps one
   voxel per axis), rotated boxes get at least 0.75 voxels of half thickness. Returns true if anything changed. */
function fixThin(r, sc, empty, fixes) {
  const done = new Set();
  for (const p of empty) {
    if (done.has(p.entry) || p.c[1] + p.e[1] <= 0) continue;
    done.add(p.entry);
    const a = r.add[p.entry];
    if (p.shape !== 'box') {
      a.shape = 'box'; delete a.axis;
      fixes.push(`add[${p.entry}] (${a.on}): zu dünn -> Quader`);
    } else {
      const size = (a.size || [1, 1, 1]).slice(), min = sc.vs * 0.75;
      for (let k = 0; k < 3; k++) if (p.h[k] < min) size[k] = sizeRange(size[k] * min / Math.max(1e-6, p.h[k]));
      a.size = size;
      fixes.push(`add[${p.entry}] (${a.on}): zu dünn -> dicker`);
    }
  }
  return done.size > 0;
}

/* 1b. entries on swinging limbs that really belong to the body: a belt on "arms", a skirt on "legs", a rifle slung
   across the chest on armR. Their copies lie mostly inside the torso/pelvis (body) or reach past the middle of the
   figure, so the limb swings them through the body. They move to the static part (pairs become one entry). */
function toStaticBody(r, t, fixes) {
  if (r.template !== 'humanoid' && r.template !== 'quadruped') return;
  const sc = buildScene(r, { t, detail: 1 }), F = sc.frames;
  const body = new Set();
  for (const p of sc.prims) if (p.base && p.group === 0) for (const k of p.cells) body.add(k);
  const replace = new Map();
  r.add.forEach((a, e) => {
    const cps = sc.prims.filter(p => p.entry === e);
    if (!cps.length || cps.some(p => p.group === 0) || (r.template === 'quadruped' && cps.some(p => p.group !== 3 && p.group !== 4 && p.group !== 5 && p.group !== 6))) return;
    let n = 0, inside = 0;
    for (const p of cps) for (const k of p.cells) { n++; if (body.has(k)) inside++; }
    let cross = false;
    if (r.template === 'humanoid') {
      // past the middle: into the other leg / more than half across the torso
      const lim = a.on.startsWith('leg') ? F.legR.c[0] : F.torso.h[0] * 0.5;
      cross = cps.some(p => (p.c[0] > 0 ? p.c[0] - p.e[0] : -(p.c[0] + p.e[0])) < -lim);
    } else {
      const lim = Math.abs(F.legFR.c[0]);
      cross = cps.some(p => (p.c[0] > 0 ? p.c[0] - p.e[0] : -(p.c[0] + p.e[0])) < -lim);
    }
    if (!(cross || (n && inside / n > 0.6))) return;
    const y = cps.reduce((s, p) => s + p.c[1], 0) / cps.length;
    const on = partForGroup(r.template, 0, y, F), Fr = F[on];
    if (!Fr) return;
    replace.set(e, rehang(a, cps, on, Fr));
    fixes.push(`add[${e}] (${a.on}): gehört zum Körper -> an ${on}`);
  });
  if (!replace.size) return;
  let room = MAX_ADD - r.add.length;
  const out = [];
  r.add.forEach((a, i) => {
    const rp = replace.get(i);
    if (rp && rp.length - 1 <= room) { room -= rp.length - 1; out.push(...rp); } else out.push(a);
  });
  r.add = out;
}

/* 2. loose copies: dock them to their own group, or re-hang them to the group they actually sit on.
   Returns CLEAN (nothing loose), CHANGED or STUCK (loose copies it could not fix). far = small moves only. */
function attachPass(r, t, detail, fixes, pass, far = false) {
  const sc = buildScene(r, { t, detail });
  const an = analyzeScene(sc);
  const { prims } = sc;
  if (!far && fixThin(r, sc, an.empty.map(i => prims[i]), fixes)) return CHANGED;
  if (!an.loose.length) return CLEAN;
  const done = new Set(), replace = new Map(), drop = new Set();
  let changed = false;
  // bigger sets first: their move decides for entries that also have tiny loose copies
  const order = an.loose.slice().sort((a, b) => b.cells - a.cells || a.prims[0] - b.prims[0]);
  for (const comp of order) {
    const ents = [...new Set(comp.prims.map(i => prims[i].entry))].filter(e => e >= 0 && !done.has(e));
    if (!ents.length) continue;
    const mv = dockMove(sc, an, comp, comp.group);
    // what else does the loose set sit on? (anchored copies of other groups)
    const other = new Map();
    for (const i of comp.prims) for (const j of an.touch[i]) if (an.anchored[j] && prims[j].group !== comp.group) other.set(prims[j].group, (other.get(prims[j].group) || 0) + 1);
    const og = [...other.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
    // far LOD: only close the small gaps the coarser voxels open up (the near LOD already decided the rest)
    if (far && (!mv || mv.dist > 2)) continue;
    if (!far && og && (!mv || mv.dist > 2) && pass < 3) {
      // it rests on another moving group: hang it there (a hip sword on the pelvis, back spikes on the tail)
      const y = comp.prims.reduce((s, i) => s + prims[i].c[1], 0) / comp.prims.length;
      const on = partForGroup(sc.tpl, og[0], y, sc.frames);
      const F = sc.frames[on];
      if (on && F) {
        for (const e of ents) {
          const copies = prims.filter(p => p.entry === e);
          if (copies.some(p => an.anchored[p.i])) continue; // partly anchored: leave it to docking
          replace.set(e, rehang(r.add[e], copies, on, F));
          done.add(e);
          fixes.push(`add[${e}] (${r.add[e].on}): sitzt auf ${on} -> dorthin umgehängt`);
          changed = true;
        }
        continue;
      }
    }
    if (!mv) continue;
    // a speck far away from everything (sparkles, a lone dot): remove instead of gluing it onto the body
    if (!far && comp.cells <= SPECK_CELLS && mv.dist > SPECK_DIST && ents.every(e => prims.every(p => p.entry !== e || !an.anchored[p.i]))) {
      for (const e of ents) { drop.add(e); done.add(e); fixes.push(`add[${e}] (${r.add[e].on}): einzelner Splitter weit weg -> entfernt`); }
      changed = true;
      continue;
    }
    const dm = mv.d.map(v => v * sc.vs);
    for (const e of ents) {
      // copy of e inside this set; mirrored twins in the same set: no sideways move
      const cps = comp.prims.map(i => prims[i]).filter(p => p.entry === e);
      const p = cps[0], d = dm.slice();
      if (cps.some(q => Math.sign(q.J[0]) !== Math.sign(p.J[0]))) d[0] = 0;
      shiftEntry(r.add[e], p, d);
      done.add(e);
      fixes.push(`add[${e}] (${r.add[e].on}): schwebte -> um ${Math.round(Math.hypot(...dm) * 100)} cm angedockt`);
      changed = true;
    }
  }
  if (replace.size || drop.size) {
    // re-hung pairs may need two entries: only while there is room (the rest is docked in the next pass)
    let room = MAX_ADD - r.add.length + drop.size;
    const out = [];
    r.add.forEach((a, i) => {
      if (drop.has(i)) return;
      const rp = replace.get(i);
      if (rp && rp.length - 1 <= room) { room -= rp.length - 1; out.push(...rp); } else out.push(a);
    });
    r.add = out;
  }
  return changed ? CHANGED : STUCK;
}

/* 3. whatever still hovers after all passes is removed (with all its copies; repeated, since a removed entry can
   leave others hanging in the air) */
function dropLoose(r, t, detail, fixes) {
  for (let k = 0; k < 4; k++) {
    const sc = buildScene(r, { t, detail });
    const an = analyzeScene(sc);
    const drop = new Set();
    for (const comp of an.loose) for (const i of comp.prims) if (sc.prims[i].entry >= 0) drop.add(sc.prims[i].entry);
    if (!drop.size) return;
    for (const e of [...drop].sort((a, b) => a - b)) fixes.push(`add[${e}] (${r.add[e].on}): schwebt weiter -> entfernt`);
    r.add = r.add.filter((a, i) => !drop.has(i));
  }
}

/**
 * Repair an own (AI) figure once: returns the repaired recipe marked with fixed = REPAIR_VERSION, or the input
 * unchanged when it is already repaired or curated (fixed set). Never throws.
 */
export function repairOwnRecipe(rec) {
  if (!rec || typeof rec !== 'object' || rec.fixed !== undefined) return rec;
  const { recipe } = repairRecipe(rec);
  return { ...recipe, fixed: REPAIR_VERSION };
}
